/**
 * Persistence for the cookie banner.
 *
 * Same key and values as the marketing site (`landing_page/src/components/
 * CookieBanner.tsx`) so the two surfaces behave alike: the choice lives in
 * `localStorage` on this origin with no expiry, and the banner stays away
 * once a choice exists. Closing with the X records nothing; it only hides
 * the banner for the current tab session, so it comes back on the next
 * visit. Storage access is wrapped because Safari private mode and
 * locked-down browsers throw on both reads and writes.
 */
export const COOKIE_CONSENT_STORAGE_KEY = 'specificai-cookie-consent';
export const COOKIE_BANNER_DISMISSED_SESSION_KEY = 'specificai-cookie-banner-dismissed';

export const COOKIE_CONSENT_VALUES = Object.freeze({
    ACCEPT: 'accept',
    REJECT: 'reject',
});

function readStorage(storage, key) {
    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
}

function writeStorage(storage, key, value) {
    try {
        storage.setItem(key, value);
    } catch {
        // Nothing to do: the banner will simply show again next time.
    }
}

/** The stored choice (`'accept'` | `'reject'`) or `null` when none was made. */
export function getCookieConsent() {
    if (typeof window === 'undefined') return null;
    const value = readStorage(window.localStorage, COOKIE_CONSENT_STORAGE_KEY);
    return Object.values(COOKIE_CONSENT_VALUES).includes(value) ? value : null;
}

export function setCookieConsent(value) {
    if (typeof window === 'undefined') return;
    writeStorage(window.localStorage, COOKIE_CONSENT_STORAGE_KEY, value);
}

export function isCookieBannerDismissedThisSession() {
    if (typeof window === 'undefined') return false;
    return readStorage(window.sessionStorage, COOKIE_BANNER_DISMISSED_SESSION_KEY) === '1';
}

export function dismissCookieBannerForSession() {
    if (typeof window === 'undefined') return;
    writeStorage(window.sessionStorage, COOKIE_BANNER_DISMISSED_SESSION_KEY, '1');
}
